import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import {
  Printer, ArrowLeft, ChevronRight, FolderOpen, CheckCircle2, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const fileUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const isImage = (w) => {
  const t = (w.file_type || "").toLowerCase();
  const u = (w.file_url || "").toLowerCase();
  return /(png|jpg|jpeg|webp|gif|heic)/.test(t) || /\.(png|jpg|jpeg|webp|gif|heic)(\?|$)/.test(u);
};

export default function PrintDesignsPortal() {
  const [projects, setProjects] = useState(null);
  const [code, setCode] = useState(null);

  useEffect(() => {
    api.get("/projects")
      .then((r) => setProjects(Array.isArray(r.data) ? r.data : (r.data.projects || [])))
      .catch(() => setProjects([]));
  }, []);

  return (
    <div className="p-5 sm:p-8 max-w-5xl mx-auto" data-testid="print-designs-page">
      {projects === null ? (
        <div className="py-24 text-center text-zinc-400">Loading…</div>
      ) : code ? (
        <ProjectDesigns code={code} onBack={() => setCode(null)} />
      ) : (
        <ProjectList projects={projects} onOpen={setCode} />
      )}
    </div>
  );
}

function ProjectList({ projects, onOpen }) {
  return (
    <div data-testid="print-designs-list">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-rose-600 text-white flex items-center justify-center">
          <Printer size={20} strokeWidth={1.8} />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">Print Designs</h1>
          <p className="text-sm text-zinc-500">Pick a project, choose designs, print a clean client-approval set.</p>
        </div>
      </div>
      {projects.length === 0 ? (
        <div className="mt-10 py-20 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl">
          No projects yet.
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (
            <button key={p.project_code} onClick={() => onOpen(p.project_code)}
              data-testid={`print-designs-project-${p.project_code}`}
              className="text-left rounded-2xl border border-zinc-200 shadow-sm bg-white p-5 hover:border-rose-300 hover:shadow-md transition-all">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={p.project_code} /></span>
                <ChevronRight size={16} className="text-zinc-300" />
              </div>
              <div className="mt-1 font-semibold text-zinc-900 line-clamp-2">{p.name}</div>
              {p.client_name && <div className="text-xs text-zinc-400">{p.client_name}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProjectDesigns({ code, onBack }) {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(() => {
    api.get(`/projects/${code}/printable-designs`)
      .then((r) => {
        setData(r.data);
        const all = new Set();
        (r.data.groups || []).forEach((g) => g.walls.forEach((w) => all.add(w.boq_id)));
        setSelected(all);
      })
      .catch((e) => { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); setData({ groups: [] }); });
  }, [code]);
  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleGroup = (walls) => setSelected((p) => {
    const n = new Set(p);
    const allOn = walls.every((w) => n.has(w.boq_id));
    walls.forEach((w) => (allOn ? n.delete(w.boq_id) : n.add(w.boq_id)));
    return n;
  });

  function openPrint() {
    if (!data) return;
    const chosen = [];
    (data.groups || []).forEach((g) =>
      g.walls.forEach((w) => { if (selected.has(w.boq_id)) chosen.push({ ...w, group: g.group }); }));
    if (chosen.length === 0) { toast.error("Select at least one design to print"); return; }
    const win = window.open("", "_blank");
    if (!win) { toast.error("Allow pop-ups to open the print view"); return; }
    const esc = (s) => String(s || "").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const pages = chosen.map((w, i) => `
      <section class="page${i === chosen.length - 1 ? " last" : ""}">
        <div class="hdr">
          <div>
            <div class="proj">${esc(data.project_name)}</div>
            <div class="client">${esc(data.client_name)} · ${esc(data.project_code)}</div>
          </div>
          <div class="grp">${esc(w.group)}</div>
        </div>
        <div class="title">${esc(w.wall_no)}${w.approved ? ' <span class="ok">APPROVED</span>' : ""}</div>
        <div class="art">${
          isImage(w)
            ? `<img src="${fileUrl(w.file_url)}" alt="${esc(w.wall_no)}" />`
            : `<div class="nonimg"><div>${esc(w.file_name) || "Design file"}</div><div class="sub">Open digitally — non-image format</div></div>`
        }</div>
      </section>`).join("");
    win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${esc(data.project_name)} — Designs</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        body { font-family: -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif; margin: 0; color: #18181b; }
        .page { width: 100%; height: 100vh; page-break-after: always; display: flex; flex-direction: column; padding: 6mm; }
        .page.last { page-break-after: auto; }
        .hdr { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #e4e4e7; padding-bottom: 6px; }
        .proj { font-size: 18px; font-weight: 700; }
        .client { font-size: 11px; color: #71717a; letter-spacing: .04em; text-transform: uppercase; }
        .grp { font-size: 12px; font-weight: 600; color: #be123c; background: #fff1f2; border: 1px solid #fecdd3; padding: 4px 10px; border-radius: 999px; }
        .title { font-size: 15px; font-weight: 600; margin: 10px 0 6px; }
        .ok { font-size: 9px; color: #15803d; background: #dcfce7; border: 1px solid #bbf7d0; padding: 2px 6px; border-radius: 999px; vertical-align: middle; margin-left: 6px; }
        .art { flex: 1; display: flex; align-items: center; justify-content: center; overflow: hidden; }
        .art img { max-width: 100%; max-height: 100%; object-fit: contain; border: 1px solid #e4e4e7; border-radius: 6px; }
        .nonimg { text-align: center; color: #a1a1aa; border: 1px dashed #d4d4d8; border-radius: 8px; padding: 40px; }
        .nonimg .sub { font-size: 11px; margin-top: 6px; }
      </style></head><body>${pages}</body></html>`);
    win.document.close();
    const trigger = () => { try { win.focus(); win.print(); } catch (e) {} };
    if (win.document.readyState === "complete") setTimeout(trigger, 600);
    else win.onload = () => setTimeout(trigger, 600);
  }

  if (data === null) return <div className="py-24 text-center text-zinc-400">Loading…</div>;
  const groups = data.groups || [];

  return (
    <div data-testid="print-designs-view">
      <button onClick={onBack} data-testid="print-designs-back"
        className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-800 mb-4">
        <ArrowLeft size={16} /> All projects
      </button>
      <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
        <div>
          <span className="font-mono text-[11px] tracking-widest text-zinc-400 uppercase"><ProjRef code={data.project_code} /></span>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">{data.project_name}</h1>
        </div>
        <button onClick={openPrint} data-testid="print-designs-print-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-zinc-900 text-white px-4 py-2.5 text-sm font-medium hover:bg-zinc-800">
          <Printer size={16} /> Print {selected.size} design(s)
        </button>
      </div>

      {groups.length === 0 ? (
        <div className="py-20 text-center text-zinc-400 border border-dashed border-zinc-200 rounded-2xl"
          data-testid="print-designs-empty">
          No client-facing designs uploaded yet for this project.
        </div>
      ) : (
        <div className="space-y-5">
          {groups.map((g) => {
            const allOn = g.walls.every((w) => selected.has(w.boq_id));
            return (
              <div key={g.group} className="rounded-2xl border border-zinc-200 shadow-sm bg-white overflow-hidden"
                data-testid={`print-designs-group-${g.group}`}>
                <button onClick={() => toggleGroup(g.walls)} data-testid={`print-designs-group-toggle-${g.group}`}
                  className="w-full flex items-center gap-2.5 px-5 py-3 bg-zinc-50 border-b border-zinc-200 text-left hover:bg-zinc-100/70">
                  <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${allOn ? "bg-zinc-900 border-zinc-900" : "border-zinc-300 bg-white"}`}>
                    {allOn && <CheckCircle2 size={11} className="text-white" />}
                  </span>
                  <FolderOpen size={18} className="text-rose-600" strokeWidth={1.6} />
                  <span className="font-semibold text-zinc-900">{g.group}</span>
                  <span className="text-xs text-zinc-400">· {g.walls.length} design(s)</span>
                </button>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                  {g.walls.map((w) => {
                    const on = selected.has(w.boq_id);
                    return (
                      <button key={w.boq_id} onClick={() => toggle(w.boq_id)} data-testid={`print-designs-wall-${w.boq_id}`}
                        className={`text-left rounded-xl border-2 overflow-hidden transition-all ${on ? "border-rose-500 ring-2 ring-rose-100" : "border-zinc-200 opacity-70 hover:opacity-100"}`}>
                        <div className="aspect-video bg-zinc-50 flex items-center justify-center overflow-hidden">
                          {isImage(w) ? (
                            <img src={fileUrl(w.file_url)} alt={w.wall_no} className="w-full h-full object-cover" />
                          ) : (
                            <ImageIcon size={26} className="text-zinc-300" />
                          )}
                        </div>
                        <div className="px-3 py-2 flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-zinc-800 truncate">{w.wall_no}</div>
                            {w.approved && <span className="text-[10px] text-emerald-600">Approved</span>}
                          </div>
                          <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? "bg-rose-600 border-rose-600" : "border-zinc-300 bg-white"}`}>
                            {on && <CheckCircle2 size={11} className="text-white" />}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
